import { Txn, Category, Group } from '../money.model';
import { spendableTxns, recentMonths, monthKey, UNCATEGORIZED_COLOR } from '../dashboard/dashboard.aggregate';
import { RecurringSeries } from '../rules/recurring.detector';
import { parentKey } from '../recurring/recurring.calc';
import { nextMonth } from '../accounts/accounts.calc';

export const MERCHANT_TOP_N = 8;
export const LARGEST_TXNS_N = 5;
/**
 * Mirrors KEY_TOKENS in rules/recurring.detector.ts: real card-swipe
 * descriptions fragment by store and city, and two tokens is the prefix that
 * groups those variants as one merchant. Declared here rather than imported
 * because the detector keeps its constant private; the comment ties them.
 */
const STEM_TOKENS = 2;
/** The 6-month window the group table averages over — same span as the dashboard trend. */
const AVG_WINDOW_MONTHS = 6;

export interface HeadlineRow { cents: number; prevCents: number | null; yoyCents: number | null; }
export interface MonthHeadline {
  income: HeadlineRow; spending: HeadlineRow; net: HeadlineRow;
  /** Rounded percent, or null when the month had no income — never a division result. */
  savingsRatePct: number | null;
}
export interface CategoryLine { id: string; name: string; cents: number; prevCents: number; avg6Cents: number; }
export interface GroupLine {
  id: string; name: string; color: string;
  cents: number; prevCents: number; avg6Cents: number;
  categories: CategoryLine[];
}
export interface MerchantLine { stem: string; display: string; cents: number; count: number; }
export interface MerchantSplit { bills: MerchantLine[]; everyday: MerchantLine[]; }
export interface YearHeadline {
  year: string; monthsPresent: string[];
  incomeCents: number; spendingCents: number; netCents: number;
  avgIncomeCents: number; avgSpendingCents: number; avgNetCents: number;
}
export interface YearCategoryLine {
  id: string; name: string; groupName: string;
  cents: number; prevCents: number | null; deltaPct: number | null;
  /** e.g. "vs Jan–Aug 2025"; null when no fair comparison exists. */
  comparedLabel: string | null;
}

// Delegates to the shared, guarded implementation (handles a dateless ghost
// record — a bulk-categorize patch applied to an id that no longer exists —
// without throwing) rather than a second local slice.
const monthOf = (t: Txn): string => monthKey(t.date);
const pad = (n: number): string => String(n).padStart(2, '0');
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (month: string): string => MONTH_SHORT[Number(month.slice(5, 7)) - 1];

export function reportMonths(txns: Txn[]): string[] {
  // A ghost record (no `date` — e.g. a bulk-categorize patch applied to an id
  // that no longer exists) must be dropped here, not counted as a phantom
  // '' month: monthOf/monthKey would return '' for it without this guard.
  return [...new Set(spendableTxns(txns ?? []).filter(t => t.date).map(monthOf))].sort().reverse();
}

export function reportYears(txns: Txn[]): string[] {
  return [...new Set(spendableTxns(txns ?? []).filter(t => t.date).map(t => t.date.slice(0, 4)))]
    .sort().reverse();
}

export function prevMonthOf(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  return m === 1 ? `${y - 1}-12` : `${y}-${pad(m - 1)}`;
}

export function sameMonthLastYear(month: string): string {
  return `${Number(month.slice(0, 4)) - 1}-${month.slice(5, 7)}`;
}

/** Signed totals for one set of rows: income sums positives, spending sums |negatives|. */
function totalsFor(rows: Txn[]): { income: number; spending: number } {
  let income = 0, spending = 0;
  rows.forEach(t => { if (t.amount > 0) income += t.amount; else spending += -t.amount; });
  return { income, spending };
}

/**
 * The month's headline figures with comparison VALUES rather than
 * percentages: net crosses zero (March 2026 netted −$861), and a percent
 * against a negative or zero base is noise. The template renders "vs $X",
 * which is honest at every sign.
 */
export function monthHeadline(txns: Txn[], month: string): MonthHeadline {
  // Dropped here: a dateless ghost record has no month to be picked into.
  const sp = spendableTxns(txns ?? []).filter(t => t.date);
  const pick = (m: string) => sp.filter(t => monthOf(t) === m);

  const cur = totalsFor(pick(month));
  const prevRows = pick(prevMonthOf(month));
  const yoyRows = pick(sameMonthLastYear(month));
  const prev = prevRows.length ? totalsFor(prevRows) : null;
  const yoy = yoyRows.length ? totalsFor(yoyRows) : null;

  const row = (get: (t: { income: number; spending: number }) => number): HeadlineRow => ({
    cents: get(cur),
    prevCents: prev ? get(prev) : null,
    yoyCents: yoy ? get(yoy) : null,
  });

  return {
    income: row(t => t.income),
    spending: row(t => t.spending),
    net: row(t => t.income - t.spending),
    savingsRatePct: cur.income > 0
      ? Math.round(((cur.income - cur.spending) / cur.income) * 100)
      : null,
  };
}

/** Spending by category for one month, keyed by categoryId ('uncategorized' for none). */
function spendByCategory(rows: Txn[]): Map<string, number> {
  const out = new Map<string, number>();
  rows.forEach(t => {
    if (t.amount >= 0) return;
    const key = t.categoryId ?? 'uncategorized';
    out.set(key, (out.get(key) ?? 0) + -t.amount);
  });
  return out;
}

/**
 * Spending groups with their categories, this month vs last vs the trailing
 * 6-month average. The average divides by window months that HAVE data —
 * a two-month-old ledger must not have its average diluted by four empty
 * months that predate it.
 */
export function groupTable(
  txns: Txn[], categories: Category[], groups: Group[], month: string,
): GroupLine[] {
  // Dropped here: a dateless ghost record cannot be placed in any month.
  const sp = spendableTxns(txns ?? []).filter(t => t.date);
  const window = recentMonths(month, AVG_WINDOW_MONTHS);
  // "Has data" means has SPENDING: an income-only month must not count as a
  // populated month, or it dilutes the 6-month average with a denominator
  // the numerator never drew from.
  const monthsWithData = new Set(sp.filter(t => t.amount < 0).map(monthOf));

  const byMonth = new Map<string, Map<string, number>>();
  window.forEach(m => byMonth.set(m, spendByCategory(sp.filter(t => monthOf(t) === m))));
  const cur = byMonth.get(month) ?? new Map<string, number>();
  const prev = spendByCategory(sp.filter(t => monthOf(t) === prevMonthOf(month)));

  const windowCount = window.filter(m => monthsWithData.has(m)).length || 1;
  const avgFor = (catId: string): number => {
    let sum = 0;
    window.forEach(m => { sum += byMonth.get(m)?.get(catId) ?? 0; });
    return Math.round(sum / windowCount);
  };

  const catById = new Map(categories.map(c => [c.id, c]));
  const groupById = new Map(groups.map(g => [g.id, g]));
  const lines: GroupLine[] = [];

  groups.filter(g => g.isSpending).forEach(g => {
    const mine = categories.filter(c => c.groupId === g.id);
    const catLines: CategoryLine[] = mine
      .map(c => ({
        id: c.id, name: c.name,
        cents: cur.get(c.id) ?? 0,
        prevCents: prev.get(c.id) ?? 0,
        avg6Cents: avgFor(c.id),
      }))
      .filter(c => c.cents > 0 || c.prevCents > 0 || c.avg6Cents > 0)
      .sort((a, b) => b.cents - a.cents);
    if (!catLines.length) return;
    lines.push({
      id: g.id, name: g.name, color: g.color,
      cents: catLines.reduce((n, c) => n + c.cents, 0),
      prevCents: catLines.reduce((n, c) => n + c.prevCents, 0),
      avg6Cents: catLines.reduce((n, c) => n + c.avg6Cents, 0),
      categories: catLines,
    });
  });

  /**
   * A key belongs in Uncategorized when it is literally uncategorized, when
   * its category was deleted (dangling categoryId), or when its category's
   * group is missing or not a spending group (a refund filed under Income,
   * say). Every key in `cur` lands in exactly one of: a spending-group
   * category line above, or here — never neither, which is what keeps
   * groupTable's total reconciled with monthHeadline's spending figure.
   */
  const isUncatKey = (k: string): boolean => {
    if (k === 'uncategorized') return true;
    const cat = catById.get(k);
    if (!cat) return true;
    const g = groupById.get(cat.groupId);
    return !g || !g.isSpending;
  };

  // The key set is built from the whole window, not just the current month —
  // a dangling categoryId that only appears in the previous month or an
  // older window month must still surface in prevCents/avg6Cents, the same
  // way a live category's line would.
  const allKeys = new Set<string>();
  cur.forEach((_, k) => allKeys.add(k));
  prev.forEach((_, k) => allKeys.add(k));
  byMonth.forEach(m => m.forEach((_, k) => allKeys.add(k)));

  const uncat = [...allKeys].filter(isUncatKey);
  if (uncat.length) {
    const sum = (m: Map<string, number>) => uncat.reduce((n, k) => n + (m.get(k) ?? 0), 0);
    const cents = sum(cur);
    const prevCents = sum(prev);
    const avg = uncat.reduce((n, k) => n + avgFor(k), 0);
    if (cents > 0 || prevCents > 0 || avg > 0) {
      lines.push({
        id: 'uncategorized', name: 'Uncategorized & other', color: UNCATEGORIZED_COLOR,
        cents, prevCents, avg6Cents: avg, categories: [],
      });
    }
  }

  return lines.sort((a, b) => b.cents - a.cents);
}

export function merchantStem(matchKey: string): string {
  return (matchKey ?? '').trim().split(/\s+/).filter(Boolean)
    .slice(0, STEM_TOKENS).join(' ');
}

/**
 * The merchant stems that belong to a detected or pinned recurring series.
 * A series key is `direction:STEM`, `direction:STEM|PRODUCT`, or a
 * billing-day child `…#DAY`; all reduce to the stem via parentKey plus
 * suffix stripping. Product-split series (GOOGLE|FIBER vs GOOGLE|CLOUD)
 * mean the whole GOOGLE stem counts as bills — a lens, not a ledger.
 */
export function billStems(series: RecurringSeries[]): Set<string> {
  const stems = new Set<string>();
  (series ?? []).forEach(s => {
    const parent = parentKey(s.key);
    const body = parent.slice(parent.indexOf(':') + 1);
    const pipe = body.indexOf('|');
    stems.add(pipe < 0 ? body : body.slice(0, pipe));
  });
  return stems;
}

/**
 * Top merchants for a period ('YYYY-MM' or 'YYYY' prefix), split into Bills
 * (stem matches a recurring series) and Everyday spending — because one list
 * is always "the mortgage, then everything else" ($25,937 of 2026), and the
 * interesting answers live below it.
 */
export function merchantSplit(
  txns: Txn[], series: RecurringSeries[], periodPrefix: string,
  topN = MERCHANT_TOP_N,
): MerchantSplit {
  const rows = spendableTxns(txns ?? [])
    .filter(t => t.amount < 0 && t.date && t.date.startsWith(periodPrefix));
  const bills = billStems(series);

  const agg = new Map<string, { cents: number; count: number; names: Map<string, number> }>();
  rows.forEach(t => {
    const stem = merchantStem(t.matchKey) || '(unknown)';
    const entry = agg.get(stem) ?? { cents: 0, count: 0, names: new Map() };
    entry.cents += -t.amount;
    entry.count += 1;
    const name = (t.merchant ?? '').trim();
    if (name) entry.names.set(name, (entry.names.get(name) ?? 0) + 1);
    agg.set(stem, entry);
  });

  const lines: MerchantLine[] = [...agg.entries()].map(([stem, e]) => {
    let display = stem, best = 0;
    e.names.forEach((n, name) => { if (n > best) { best = n; display = name; } });
    return { stem, display, cents: e.cents, count: e.count };
  }).sort((a, b) => b.cents - a.cents);

  return {
    bills: lines.filter(l => bills.has(l.stem)).slice(0, topN),
    everyday: lines.filter(l => !bills.has(l.stem)).slice(0, topN),
  };
}

/** The period's biggest spending rows, income excluded. */
export function largestTxns(txns: Txn[], periodPrefix: string, n = LARGEST_TXNS_N): Txn[] {
  return spendableTxns(txns ?? [])
    .filter(t => t.amount < 0 && t.date && t.date.startsWith(periodPrefix))
    .sort((a, b) => a.amount - b.amount)
    .slice(0, n);
}

export function yearHeadline(txns: Txn[], year: string): YearHeadline {
  const sp = spendableTxns(txns ?? []).filter(t => t.date && t.date.startsWith(year));
  const monthsPresent = [...new Set(sp.map(monthOf))].sort();
  const { income, spending } = totalsFor(sp);
  // Averages divide by months present, never by 12 — a partial year's
  // "monthly average" must describe the months that exist.
  const n = monthsPresent.length || 1;
  return {
    year, monthsPresent,
    incomeCents: income, spendingCents: spending, netCents: income - spending,
    avgIncomeCents: Math.round(income / n),
    avgSpendingCents: Math.round(spending / n),
    avgNetCents: Math.round((income - spending) / n),
  };
}

/**
 * Net per month, chart-ready, contiguous from the year's first to its last
 * present month — a missing month must render as a zero point, not a gap
 * mm-line silently compresses out of the time axis. Negative months survive
 * unclamped — mm-line scales min→max. Same reasoning as monthEndBalances on
 * the Accounts screen, except a month's net is a flow, so an absent month is
 * zero rather than carried forward.
 */
export function netByMonth(txns: Txn[], year: string): { month: string; label: string; cents: number }[] {
  const sp = spendableTxns(txns ?? []).filter(t => t.date && t.date.startsWith(year));
  const byM = new Map<string, number>();
  sp.forEach(t => byM.set(monthOf(t), (byM.get(monthOf(t)) ?? 0) + t.amount));

  const present = [...byM.keys()].sort();
  if (!present.length) return [];

  const out: { month: string; label: string; cents: number }[] = [];
  const last = present[present.length - 1];
  let month = present[0];
  while (true) {
    out.push({ month, label: monthLabel(month), cents: byM.get(month) ?? 0 });
    if (month === last) break;
    month = nextMonth(month);
  }
  return out;
}

/**
 * Whether a fair year-over-year comparison exists for `year`, and why not
 * when it doesn't. Shared by yearCategoryTable (which needs the fairness
 * flag plus the month bookkeeping to build the comparison) and
 * yoyCoverageNote (which needs only the human-readable reason) so the two
 * can never drift apart on what "fair" means.
 *
 * Fair requires BOTH: the previous year has spending data for every calendar
 * month the current year has, AND every account with spending in the current
 * period also had spending in the compared period. Calendar coverage alone
 * is not enough — on the real ledger, apple-card only started importing in
 * 2025-12, so 2026 Jan–Aug compares two accounts' spending against 2025
 * Jan–Aug's one account, inflating deltas (Groceries +618%) that are an
 * import artifact, not behaviour.
 */
function yoyFairness(sp: Txn[], year: string): {
  months: string[]; mms: string[]; prevYear: string; prevMonthsWanted: string[];
  fair: boolean; note: string | null;
} {
  // Spending-only on BOTH sides of the comparison, matching the doc above:
  // an income-only month must not license (or be compared against) a month
  // that had no spending at all.
  const months = [...new Set(
    sp.filter(t => t.amount < 0 && t.date.startsWith(year)).map(monthOf))].sort();
  const mms = months.map(m => m.slice(5, 7));
  const prevYear = String(Number(year) - 1);
  const prevMonthsWanted = mms.map(mm => `${prevYear}-${mm}`);

  if (!months.length) {
    return { months, mms, prevYear, prevMonthsWanted, fair: false, note: null };
  }

  // "Has data" means has SPENDING (amount < 0), not merely a row — an
  // income-only month must not license a comparison against a month that
  // had no spending at all.
  const prevMonthsPresent = new Set(
    sp.filter(t => t.amount < 0 && t.date.startsWith(prevYear)).map(monthOf));
  const missingMonths = prevMonthsWanted.filter(m => !prevMonthsPresent.has(m));
  if (missingMonths.length) {
    const label = missingMonths.map(monthLabel).join(', ');
    return {
      months, mms, prevYear, prevMonthsWanted, fair: false,
      note: `No year-over-year comparison: ${prevYear} has no spending data for ${label}.`,
    };
  }

  const curAccounts = new Set(
    sp.filter(t => t.amount < 0 && months.includes(monthOf(t))).map(t => t.accountId));
  const prevAccounts = new Set(
    sp.filter(t => t.amount < 0 && prevMonthsWanted.includes(monthOf(t))).map(t => t.accountId));
  const coveredCount = [...curAccounts].filter(a => prevAccounts.has(a)).length;

  if (coveredCount < curAccounts.size) {
    const contiguous = months.every((m, i) => i === 0 || m === nextMonth(months[i - 1]));
    const rangeLabel = mms.length === 12
      ? prevYear
      : contiguous
        ? `${monthLabel(months[0])}–${monthLabel(months[months.length - 1])} ${prevYear}`
        : `${mms.length} months in ${prevYear}`;
    return {
      months, mms, prevYear, prevMonthsWanted, fair: false,
      note: `No year-over-year comparison: ${rangeLabel} covers ${coveredCount} of the `
        + `${curAccounts.size} accounts active in ${year}.`,
    };
  }

  return { months, mms, prevYear, prevMonthsWanted, fair: true, note: null };
}

/** Why a year has no fair year-over-year comparison, or null when it does. */
export function yoyCoverageNote(txns: Txn[], year: string): string | null {
  return yoyFairness(spendableTxns(txns ?? []).filter(t => t.date), year).note;
}

/**
 * Category spending for the year with a LIKE-FOR-LIKE YoY delta: the same
 * calendar months of the previous year, and only when the previous year has
 * data for every one of them AND covers every account the current period
 * covers (see yoyFairness) — 2026's Jan–Aug against 2025's Jan–Aug, never a
 * partial year, and never a period that imported fewer accounts.
 */
export function yearCategoryTable(
  txns: Txn[], categories: Category[], groups: Group[], year: string,
): YearCategoryLine[] {
  // Dropped here: a dateless ghost record cannot be placed in any year/month.
  const sp = spendableTxns(txns ?? []).filter(t => t.date);
  const { months, mms, prevYear, prevMonthsWanted, fair } = yoyFairness(sp, year);
  if (!months.length) return [];

  const cur = spendByCategory(sp.filter(t => t.date.startsWith(year)));
  const prev = fair
    ? spendByCategory(sp.filter(t => prevMonthsWanted.some(m => monthOf(t) === m)))
    : null;

  const contiguous = months.every((m, i) => i === 0 || m === nextMonth(months[i - 1]));
  const comparedLabel = fair
    ? (mms.length === 12
        ? `vs ${prevYear}`
        : contiguous
          ? `vs ${monthLabel(months[0])}–${monthLabel(months[months.length - 1])} ${prevYear}`
          : `vs ${mms.length} months in ${prevYear}`)
    : null;

  const groupById = new Map(groups.map(g => [g.id, g]));
  const catById = new Map(categories.map(c => [c.id, c]));

  // A category with prior-year spend but nothing this year must still
  // appear (cents: 0) — "we stopped spending on this" is real information,
  // not the absence of a row. Only sound when a fair comparison exists;
  // when it doesn't, `prev` is null and there is nothing to add.
  const allIds = new Set(cur.keys());
  if (prev) prev.forEach((_, id) => allIds.add(id));

  return [...allIds].map((id): YearCategoryLine => {
    const cents = cur.get(id) ?? 0;
    const cat = catById.get(id);
    const prevCents = prev ? (prev.get(id) ?? 0) : null;
    return {
      id,
      name: cat ? cat.name : 'Uncategorized',
      groupName: cat ? (groupById.get(cat.groupId)?.name ?? '—') : '—',
      cents,
      prevCents,
      deltaPct: prevCents !== null && prevCents > 0
        ? Math.round(((cents - prevCents) / prevCents) * 100)
        : null,
      comparedLabel: prevCents !== null ? comparedLabel : null,
    };
  }).sort((a, b) => b.cents - a.cents);
}
